// usePDA1070_R01Store.ts
import { create } from "zustand";
import {
  PDA1070_R01HeaderDto,
  PDA1070_R01SearchParams,
  PDA1070_R01Dto,
  PDA1070_R01BomLotDto,
  SelectedBomLot,
} from "./PDA1070_R01Dto";

export interface PDA1070_R01Store {
  // ========== 목록 화면 ==========
  workOrderList: PDA1070_R01HeaderDto[];
  loading: boolean;
  searchParams: PDA1070_R01SearchParams;

  // ========== 상세 화면 ==========
  showList: boolean;
  selectedWorkOrder: PDA1070_R01HeaderDto | null;
  workOrderDetail: PDA1070_R01Dto | null;

  // ========== LOT 스캔 (DetailInfo 폼) ==========
  lotNo: string;
  scannedBomLot: PDA1070_R01BomLotDto | null;

  // ========== 검수 대기 (선택된 LOT + 출고량) ==========
  selectedBomLots: SelectedBomLot[];

  // ========== Actions: 목록 화면 ==========
  setWorkOrderList: (list: PDA1070_R01HeaderDto[]) => void;
  setLoading: (loading: boolean) => void;
  setSearchParams: (params: PDA1070_R01SearchParams) => void;
  updateSearchParam: (
    field: keyof PDA1070_R01SearchParams,
    value: string
  ) => void;
  resetSearchParams: () => void;

  // ========== Actions: 상세 화면 ==========
  setShowList: (show: boolean) => void;
  setSelectedWorkOrder: (order: PDA1070_R01HeaderDto | null) => void;
  setWorkOrderDetail: (detail: PDA1070_R01Dto | null) => void;

  // ========== Actions: LOT 스캔 ==========
  setLotNo: (v: string) => void;
  setScannedBomLot: (b: PDA1070_R01BomLotDto | null) => void;

  // ========== Actions: 검수 대기 ==========
  addSelectedBomLot: (lot: SelectedBomLot) => void;
  removeSelectedBomLot: (lotNo: string) => void;
  clearSelectedBomLots: () => void;

  // ========== Actions: 초기화 ==========
  resetDetail: () => void;
  reset: () => void;
}

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

const initialSearchParams: PDA1070_R01SearchParams = {
  startDate: formatLocalDate(firstDay),  // ✅ "2026-02-01"
  endDate: formatLocalDate(today),       // ✅ "2026-02-02"
  productWorkNo: "",
  workplaceCd: "",
  itemCd: "",
};

const usePDA1070_R01Store = create<PDA1070_R01Store>((set) => ({
  workOrderList: [],
  loading: false,
  searchParams: initialSearchParams,
  showList: true,
  selectedWorkOrder: null,
  workOrderDetail: null,
  lotNo: "",
  scannedBomLot: null,
  selectedBomLots: [],

  setWorkOrderList: (list) => set({ workOrderList: list }),
  setLoading: (loading) => set({ loading }),
  setSearchParams: (params) => set({ searchParams: params }),
  updateSearchParam: (field, value) =>
    set((state) => ({
      searchParams: { ...state.searchParams, [field]: value },
    })),
  resetSearchParams: () => set({ searchParams: initialSearchParams }),

  setShowList: (show) => set({ showList: show }),
  setSelectedWorkOrder: (order) => set({ selectedWorkOrder: order }),
  setWorkOrderDetail: (detail) => set({ workOrderDetail: detail }),

  setLotNo: (v) => set({ lotNo: v }),
  setScannedBomLot: (b) => set({ scannedBomLot: b }),

  addSelectedBomLot: (lot) =>
    set((state) => ({
      selectedBomLots: [...state.selectedBomLots, lot],
    })),
  removeSelectedBomLot: (lotNo) =>
    set((state) => ({
      selectedBomLots: state.selectedBomLots.filter((m) => m.lotNo !== lotNo),
    })),
  clearSelectedBomLots: () => set({ selectedBomLots: [] }),

  resetDetail: () =>
    set({
      selectedWorkOrder: null,
      workOrderDetail: null,
      lotNo: "",
      scannedBomLot: null,
      selectedBomLots: [],
    }),

  reset: () =>
    set({
      workOrderList: [],
      loading: false,
      searchParams: initialSearchParams,
      showList: true,
      selectedWorkOrder: null,
      workOrderDetail: null,
      lotNo: "",
      scannedBomLot: null,
      selectedBomLots: [],
    }),
}));

export default usePDA1070_R01Store;
